/**
 * The request variables, regrouped into what a reader asks of them: which
 * entities, broken down how, under which filters, with which settings.
 *
 * Shape-tolerant rather than per-query: the V3 requests all travel as
 * `{ req: { entities, filters, breakdown_options, … } }` but the summary
 * documents (`CompositionSummaryV3Data`) spread the same keys at the top
 * level, and anything it does not recognise still lands in Settings.
 */

type RequestGroupTitle = 'Entities' | 'Breakdown' | 'Filters' | 'Settings';

export type RequestField = {
  /** The key as it travels, for the reader who knows the API. */
  key: string;
  /** What the key means — `role_level_2` → `Role K50`. */
  label: string;
  /** Where the value sits in the variables, for the chip tooltips. */
  path: string;
  /** Rendered value. Empty when the field is an id list drawn as chips. */
  display: string;
  /** True when the value is a list of entity ids the drawer renders as chips. */
  isIdList: boolean;
};

export type RequestGroup = { title: RequestGroupTitle; fields: RequestField[] };

type SkillLevelName = (apiLevel: number) => string | undefined;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const humanise = (key: string) =>
  key.replace(/_/g, ' ').replace(/^\w/, (first) => first.toUpperCase());

/**
 * `role_level_N` ↔ `role_kX` as `role-skill-ent-kind.ts` sends them. Duplicated
 * as a plain table rather than imported: that module pulls in the filter
 * utilities, and the inspector must stay out of their import graph.
 */
const ROLE_LEVEL_K: Record<number, number> = {
  1: 7,
  2: 50,
  3: 150,
  4: 500,
  5: 1500,
  6: 5000,
  7: 15000,
};
const ACTIVITY_LEVEL_K: Record<number, number> = {
  1: 15,
  2: 50,
  3: 150,
  4: 500,
  5: 1500,
  6: 5000,
};

const GEOGRAPHY_KEYS: Record<string, string> = {
  msa: 'Metro area (MSA)',
  county: 'County',
  state: 'State',
  country: 'Country',
  region: 'Region',
  city: 'City',
};

const labelFilterKey = (key: string, skillLevelName: SkillLevelName) => {
  const level = /^(role|skills|activities)_level_(\d+)$/.exec(key);
  if (level) {
    const [, family, index] = level;
    const n = Number(index);
    if (family === 'role') {
      const k = ROLE_LEVEL_K[n];
      return k === undefined ? `Role level ${n}` : `Role K${k}`;
    }
    if (family === 'activities') {
      const k = ACTIVITY_LEVEL_K[n];
      return k === undefined ? `Activity level ${n}` : `Activity K${k}`;
    }
    return skillLevelName(n) ?? `Skill level ${n}`;
  }
  const rics = /^rics_k(\d+)$/.exec(key);
  if (rics) return `Industry K${rics[1]}`;
  return GEOGRAPHY_KEYS[key] ?? humanise(key);
};

const isIdList = (value: unknown) =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((item) => typeof item === 'number' || typeof item === 'string');

const MAX_DISPLAY_LENGTH = 120;

const displayValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  const json = JSON.stringify(value);
  return json.length > MAX_DISPLAY_LENGTH
    ? `${json.slice(0, MAX_DISPLAY_LENGTH)}…`
    : json;
};

/** `{ role: true, role_breakdown_level: 2, role_top_n: 10 }` → `Role: level 2, top 10`. */
const breakdownFields = (
  options: Record<string, unknown>,
  path: string
): RequestField[] => {
  const fields: RequestField[] = [];
  for (const [key, value] of Object.entries(options)) {
    if (value !== true) continue;
    const detail: string[] = [];
    const level = options[`${key}_breakdown_level`];
    if (level !== undefined) detail.push(`level ${displayValue(level)}`);
    const topN = options[`${key}_top_n`];
    if (topN !== undefined) detail.push(`top ${displayValue(topN)}`);
    fields.push({
      key,
      label: humanise(key),
      path: `${path}.${key}`,
      display: detail.join(', ') || 'yes',
      isIdList: false,
    });
  }
  return fields;
};

const joinPath = (base: string, key: string) =>
  base.length === 0 ? key : `${base}.${key}`;

export const summariseRequest = (
  variables: Record<string, unknown>,
  skillLevelName: SkillLevelName
): RequestGroup[] => {
  const req = variables['req'];
  const [root, base] = isPlainObject(req) ? [req, 'req'] : [variables, ''];

  const entities: RequestField[] = [];
  const breakdown: RequestField[] = [];
  const filters: RequestField[] = [];
  const settings: RequestField[] = [];

  for (const [key, value] of Object.entries(root)) {
    const path = joinPath(base, key);

    if (key === 'entities' && Array.isArray(value)) {
      entities.push({
        key,
        label: 'Entities',
        path,
        display: '',
        isIdList: true,
      });
      continue;
    }

    if (key === 'filters' && isPlainObject(value)) {
      let dateFrom: string | undefined;
      let dateTo: string | undefined;
      for (const [filterKey, filterValue] of Object.entries(value)) {
        if (filterValue === undefined || filterValue === null) continue;
        if (filterKey === 'start_date') {
          dateFrom = displayValue(filterValue);
          continue;
        }
        if (filterKey === 'end_date') {
          dateTo = displayValue(filterValue);
          continue;
        }
        filters.push({
          key: filterKey,
          label: labelFilterKey(filterKey, skillLevelName),
          path: `${path}.${filterKey}`,
          display: isIdList(filterValue) ? '' : displayValue(filterValue),
          isIdList: isIdList(filterValue),
        });
      }
      if (dateFrom !== undefined || dateTo !== undefined) {
        filters.unshift({
          key: 'dates',
          label: 'Dates',
          path: `${path}.start_date`,
          display: `${dateFrom ?? '…'} → ${dateTo ?? '…'}`,
          isIdList: false,
        });
      }
      continue;
    }

    if (key === 'breakdown_options' && isPlainObject(value)) {
      breakdown.push(...breakdownFields(value, path));
      continue;
    }

    settings.push({
      key,
      label: humanise(key),
      path,
      display: displayValue(value),
      isIdList: false,
    });
  }

  const groups: RequestGroup[] = [
    { title: 'Entities', fields: entities },
    { title: 'Breakdown', fields: breakdown },
    { title: 'Filters', fields: filters },
    { title: 'Settings', fields: settings },
  ];
  return groups.filter((group) => group.fields.length > 0);
};
